/**
 * The add/edit form for a single account in the chart of accounts.
 *
 * One component serves both modes so the rules an owner learns while adding an
 * account still hold while editing one. Validation runs twice on purpose:
 * inline in the browser for immediate feedback, and again on the server, whose
 * `details.fields` errors are merged into the same slots — the field that is
 * wrong is always the field that is highlighted.
 */

import { useEffect, useId, useMemo, useState } from 'react';

import { ApiRequestError } from '../../lib/api-client';

/** Account classes, mirrored from `packages/db/src/schema/accounts.ts`. */
export type AccountType = 'asset' | 'liability' | 'equity' | 'income' | 'expense';

export interface AccountTypeOption {
  type: AccountType;
  label: string;
  codePrefix: string;
  normalBalance: 'debit' | 'credit';
  summary: string;
}

export interface AccountUsage {
  postedLines: number;
  draftLines: number;
  childAccounts: number;
  canDelete: boolean;
}

/** One row of `GET /api/accounts`. */
export interface AccountRow {
  id: string;
  code: string;
  name: string;
  type: AccountType;
  normalBalance: 'debit' | 'credit';
  subtype: string;
  parentId: string | null;
  description: string;
  isSystem: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  updatedBySubject: string | null;
  usage?: AccountUsage;
}

export interface Paginated<TRow> {
  data: TRow[];
  page: number;
  pageSize: number;
  total: number;
}

export interface AccountFormValues {
  code: string;
  name: string;
  type: AccountType;
  subtype: string;
  description: string;
}

export const ACCOUNT_CODE_MAX_LENGTH = 16;
export const ACCOUNT_NAME_MAX_LENGTH = 120;
export const ACCOUNT_DESCRIPTION_MAX_LENGTH = 280;
const ACCOUNT_CODE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]*$/u;

export const EMPTY_ACCOUNT_FORM: AccountFormValues = {
  code: '',
  name: '',
  type: 'expense',
  subtype: '',
  description: '',
};

/** Pull `details.fields` off an API error envelope; `{}` for anything else. */
export function fieldErrors(error: unknown): Record<string, string[]> {
  if (!(error instanceof ApiRequestError)) {
    return {};
  }
  const fields = error.details?.['fields'];
  return typeof fields === 'object' && fields !== null ? (fields as Record<string, string[]>) : {};
}

/** A sentence a person can act on — never a stack trace, never "[object Object]". */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) {
    return error.message;
  }
  return 'Something went wrong. Try again, and tell an owner if it keeps happening.';
}

export function FieldError({
  messages,
  id,
}: {
  messages: string[] | undefined;
  id?: string | undefined;
}): JSX.Element | null {
  if (messages === undefined || messages.length === 0) {
    return null;
  }
  return (
    <p id={id} className="mt-1 text-xs font-medium text-state-negative" role="alert">
      {messages.join(' ')}
    </p>
  );
}

/** Client-side rules. Kept deliberately identical to the server's. */
export function validateAccountForm(values: AccountFormValues): Record<string, string[]> {
  const errors: Record<string, string[]> = {};
  const code = values.code.trim();
  const name = values.name.trim();

  if (code === '') {
    errors['code'] = ['Enter a GL code.'];
  } else if (code.length > ACCOUNT_CODE_MAX_LENGTH) {
    errors['code'] = [`A GL code can be at most ${String(ACCOUNT_CODE_MAX_LENGTH)} characters.`];
  } else if (!ACCOUNT_CODE_PATTERN.test(code)) {
    errors['code'] = [
      'Use letters, digits, dots, dashes, or underscores — for example 6810 or 1000-01.',
    ];
  }

  if (name === '') {
    errors['name'] = ['Enter an account name.'];
  } else if (name.length > ACCOUNT_NAME_MAX_LENGTH) {
    errors['name'] = [`Keep the name under ${String(ACCOUNT_NAME_MAX_LENGTH)} characters.`];
  }

  if (values.description.length > ACCOUNT_DESCRIPTION_MAX_LENGTH) {
    errors['description'] = [
      `Keep the description under ${String(ACCOUNT_DESCRIPTION_MAX_LENGTH)} characters.`,
    ];
  }

  return errors;
}

const FIELD_CLASS =
  'mt-1 min-h-touch w-full rounded-md border border-line-strong bg-surface-raised px-3 text-sm text-ink-900 transition-colors duration-150 ease-standard focus:border-brand-red';
const INVALID_FIELD_CLASS =
  'mt-1 min-h-touch w-full rounded-md border border-state-negative bg-state-negative-soft px-3 text-sm text-ink-900';

export interface AccountFormProps {
  mode: 'create' | 'edit';
  initialValues?: AccountFormValues | undefined;
  accountTypes: readonly AccountTypeOption[];
  /** True when the account backs an automatic posting — its type is locked. */
  isSystem?: boolean | undefined;
  /** Posted ledger lines; > 0 locks the type so history stays coherent. */
  postedLines?: number | undefined;
  isPending: boolean;
  serverError: unknown;
  submitLabel: string;
  onSubmit: (values: AccountFormValues) => void;
  onCancel?: (() => void) | undefined;
}

export function AccountForm({
  mode,
  initialValues,
  accountTypes,
  isSystem = false,
  postedLines = 0,
  isPending,
  serverError,
  submitLabel,
  onSubmit,
  onCancel,
}: AccountFormProps): JSX.Element {
  const baseId = useId();
  const [values, setValues] = useState<AccountFormValues>(initialValues ?? EMPTY_ACCOUNT_FORM);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    setValues(initialValues ?? EMPTY_ACCOUNT_FORM);
    setTouched(false);
  }, [initialValues]);

  const clientErrors = useMemo(() => validateAccountForm(values), [values]);
  const serverErrors = fieldErrors(serverError);
  const errors: Record<string, string[]> = touched
    ? { ...serverErrors, ...clientErrors }
    : serverErrors;

  const typeLocked = postedLines > 0;
  const selectedType = accountTypes.find((option) => option.type === values.type);
  const fieldId = (name: string): string => `${baseId}-${name}`;

  const update = <TKey extends keyof AccountFormValues>(
    key: TKey,
    value: AccountFormValues[TKey],
  ): void => {
    setValues((current) => ({ ...current, [key]: value }));
  };

  const inputClass = (name: string): string =>
    errors[name] === undefined ? FIELD_CLASS : INVALID_FIELD_CLASS;

  return (
    <form
      className="space-y-4"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        setTouched(true);
        if (Object.keys(validateAccountForm(values)).length > 0) {
          return;
        }
        onSubmit({
          ...values,
          code: values.code.trim(),
          name: values.name.trim(),
          subtype: values.subtype.trim(),
          description: values.description.trim(),
        });
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <label htmlFor={fieldId('code')} className="block text-sm font-medium text-ink-700">
            GL code
          </label>
          <input
            id={fieldId('code')}
            className={`${inputClass('code')} font-mono`}
            value={values.code}
            inputMode="text"
            autoComplete="off"
            maxLength={ACCOUNT_CODE_MAX_LENGTH}
            placeholder={selectedType === undefined ? '6810' : `${selectedType.codePrefix}xxx`}
            aria-invalid={errors['code'] !== undefined}
            aria-describedby={errors['code'] === undefined ? undefined : fieldId('code-error')}
            onChange={(event) => update('code', event.target.value)}
            onBlur={() => setTouched(true)}
          />
          <FieldError messages={errors['code']} id={fieldId('code-error')} />
        </div>

        <div className="sm:col-span-1 lg:col-span-2">
          <label htmlFor={fieldId('name')} className="block text-sm font-medium text-ink-700">
            Account name
          </label>
          <input
            id={fieldId('name')}
            className={inputClass('name')}
            value={values.name}
            maxLength={ACCOUNT_NAME_MAX_LENGTH}
            placeholder="Merchant Processing Fees"
            aria-invalid={errors['name'] !== undefined}
            aria-describedby={errors['name'] === undefined ? undefined : fieldId('name-error')}
            onChange={(event) => update('name', event.target.value)}
            onBlur={() => setTouched(true)}
          />
          <FieldError messages={errors['name']} id={fieldId('name-error')} />
        </div>

        <div>
          <label htmlFor={fieldId('type')} className="block text-sm font-medium text-ink-700">
            Type
          </label>
          <select
            id={fieldId('type')}
            className={inputClass('type')}
            value={values.type}
            disabled={typeLocked}
            aria-describedby={fieldId('type-hint')}
            onChange={(event) => update('type', event.target.value as AccountType)}
          >
            {accountTypes.map((option) => (
              <option key={option.type} value={option.type}>
                {option.label}
              </option>
            ))}
          </select>
          <p id={fieldId('type-hint')} className="mt-1 text-xs text-ink-500">
            {typeLocked
              ? `Locked — ${String(postedLines)} posted ledger line${postedLines === 1 ? '' : 's'} depend on this type.`
              : selectedType === undefined
                ? 'Choose an account type.'
                : `Increases with a ${selectedType.normalBalance}.`}
          </p>
          <FieldError messages={errors['type']} />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor={fieldId('subtype')} className="block text-sm font-medium text-ink-700">
            Grouping <span className="font-normal text-ink-500">(optional)</span>
          </label>
          <input
            id={fieldId('subtype')}
            className={inputClass('subtype')}
            value={values.subtype}
            maxLength={80}
            placeholder="Cash and bank"
            onChange={(event) => update('subtype', event.target.value)}
          />
          <p className="mt-1 text-xs text-ink-500">
            Section heading this account appears under in reports.
          </p>
          <FieldError messages={errors['subtype']} />
        </div>

        <div>
          <label
            htmlFor={fieldId('description')}
            className="block text-sm font-medium text-ink-700"
          >
            What belongs here <span className="font-normal text-ink-500">(optional)</span>
          </label>
          <input
            id={fieldId('description')}
            className={inputClass('description')}
            value={values.description}
            maxLength={ACCOUNT_DESCRIPTION_MAX_LENGTH}
            placeholder="Card and ACH processing fees"
            onChange={(event) => update('description', event.target.value)}
          />
          <FieldError messages={errors['description']} />
        </div>
      </div>

      {isSystem && (
        <p className="rounded-md border border-state-caution bg-state-caution-soft p-3 text-sm text-ink-900">
          Torvanis Money posts to this account automatically. Renaming or re-coding it is safe;
          deleting it is not, so only disabling is offered.
        </p>
      )}

      {serverError != null && Object.keys(serverErrors).length === 0 && (
        <p
          role="alert"
          className="rounded-md border border-state-negative bg-state-negative-soft p-3 text-sm text-ink-900"
        >
          {errorMessage(serverError)}
        </p>
      )}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        {onCancel !== undefined && (
          <button type="button" className="tvm-button-secondary" onClick={onCancel}>
            Cancel
          </button>
        )}
        <button type="submit" className="tvm-button-primary" disabled={isPending}>
          {isPending ? 'Saving…' : submitLabel}
        </button>
      </div>

      <p className="sr-only" aria-live="polite">
        {mode === 'create' ? 'Adding a new account' : 'Editing an existing account'}
      </p>
    </form>
  );
}
